import { ChevronDown, Headphones } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { NetworkQuality } from "../hooks/useATClient";
import { BottomSheet } from "./BottomSheet";
import { cn } from "../lib/cn";

// ─── Country flag helper ───────────────────────────────────────────────────────

function flagEmoji(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return "🌍";
  return countryCode
    .toUpperCase()
    .split("")
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join("");
}

// ─── Network quality dot ──────────────────────────────────────────────────────

function NetworkDot({ quality }: { quality: NetworkQuality }) {
  const color =
    quality === "good"
      ? "bg-emerald-500"
      : quality === "degraded"
        ? "bg-amber-400"
        : quality === "poor"
          ? "bg-destructive"
          : "bg-muted-foreground/50";

  return (
    <span
      className={cn(
        "absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white",
        color,
      )}
    />
  );
}

function qualityLabel(q: NetworkQuality): string {
  if (q === "good") return "Good";
  if (q === "degraded") return "Degraded";
  if (q === "poor") return "Poor";
  return "Checking…";
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DialerNumber {
  id: string;
  number: string;
  country_code: string | null;
}

interface DialerHeaderProps {
  numbers: DialerNumber[];
  selectedNumberId: string | null;
  onSelectNumber: (id: string) => void;
  networkQuality: NetworkQuality;
  /** Controls whether the "number I'm using" row is visible (dialpad tab only). */
  showNumberSelector: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DialerHeader({
  numbers,
  selectedNumberId,
  onSelectNumber,
  networkQuality,
  showNumberSelector,
}: DialerHeaderProps) {
  const [numberSheetOpen, setNumberSheetOpen] = useState(false);
  const [showDeviceInfo, setShowDeviceInfo] = useState(false);
  const [micLabel, setMicLabel] = useState<string | null>(null);
  const [speakerLabel, setSpeakerLabel] = useState<string | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const selectedNumber = numbers.find((n) => n.id === selectedNumberId) ?? numbers[0] ?? null;

  // Enumerate audio devices when the tooltip is opened.
  const handleHeadsetClick = () => {
    setShowDeviceInfo((v) => !v);
    if (navigator.mediaDevices?.enumerateDevices) {
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          const mic = devices.find((d) => d.kind === "audioinput");
          const speaker = devices.find((d) => d.kind === "audiooutput");
          setMicLabel(mic?.label || "Default microphone");
          setSpeakerLabel(speaker?.label || "Default speaker");
        })
        .catch(() => {
          setMicLabel("Default microphone");
          setSpeakerLabel("Default speaker");
        });
    } else {
      setMicLabel("Default microphone");
      setSpeakerLabel("Default speaker");
    }
  };

  // Close tooltip on outside click.
  useEffect(() => {
    if (!showDeviceInfo) return;
    const handler = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        setShowDeviceInfo(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDeviceInfo]);

  return (
    <header
      className="shrink-0 px-4 pb-4 pt-3"
      style={{ backgroundColor: "hsl(var(--background))" }}
    >
      {/* ── Row 1: headset icon right-aligned ── */}
      <div className="flex justify-end">
        <div ref={tooltipRef} className="relative">
          <button
            onClick={handleHeadsetClick}
            className="relative flex h-9 w-9 items-center justify-center rounded-full bg-white transition-colors hover:bg-muted"
            aria-label="Audio devices and connection quality"
          >
            <Headphones className="h-4 w-4 text-muted-foreground" />
            <NetworkDot quality={networkQuality} />
          </button>

          {showDeviceInfo && (
            <div className="absolute right-0 top-11 z-50 w-52 rounded-xl border border-border bg-card p-3 shadow-lg">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs font-semibold text-foreground">Audio devices</p>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px] font-medium",
                    networkQuality === "good"
                      ? "bg-emerald-100 text-emerald-700"
                      : networkQuality === "degraded"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-red-100 text-red-700",
                  )}
                >
                  {qualityLabel(networkQuality)}
                </span>
              </div>
              <div className="space-y-2 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium text-foreground">Microphone</span>
                  <p className="mt-0.5 truncate">{micLabel ?? "Detecting…"}</p>
                </div>
                <div>
                  <span className="font-medium text-foreground">Speaker</span>
                  <p className="mt-0.5 truncate">{speakerLabel ?? "Detecting…"}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Row 2: centered label + number pill — dialpad tab only ── */}
      {showNumberSelector && numbers.length === 0 ? (
        <p className="mt-2 text-center text-sm text-muted-foreground">No numbers assigned</p>
      ) : showNumberSelector ? (
        <div className="mt-2 flex flex-col items-center gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            The number I&rsquo;m using
          </p>
          <button
            onClick={() => setNumberSheetOpen(true)}
            className="flex w-[75%] max-w-[260px] items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-foreground shadow-sm active:scale-[0.98]"
          >
            {selectedNumber && (
              <span className="shrink-0 text-base leading-none">
                {flagEmoji(selectedNumber.country_code ?? "")}
              </span>
            )}
            <span className="min-w-0 truncate">
              {selectedNumber ? selectedNumber.number : "Select number"}
            </span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </button>
        </div>
      ) : null}

      {/* ── Number picker bottom sheet ── */}
      <BottomSheet
        open={numberSheetOpen}
        onClose={() => setNumberSheetOpen(false)}
        title="Select number"
      >
        <ul className="pb-6">
          {numbers.map((n) => {
            const isSelected = n.id === selectedNumberId;
            return (
              <li key={n.id}>
                <button
                  onClick={() => {
                    onSelectNumber(n.id);
                    setNumberSheetOpen(false);
                  }}
                  className={cn(
                    "flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-muted/60 active:bg-muted",
                    isSelected && "bg-primary/5",
                  )}
                >
                  <span className="text-2xl leading-none">{flagEmoji(n.country_code ?? "")}</span>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                    {n.number}
                  </span>
                  {isSelected && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 shrink-0 text-primary"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </BottomSheet>
    </header>
  );
}
